import React, { useContext, useCallback, useRef, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import idsWithoutImages from 'components/Dashboard/KeyUpdates/idsWithoutImages';
import normalize from 'components/Dashboard/utilities/normalizeString';
import { Link, useLocation } from 'react-router-dom';
import createNotification from 'components/Settings/Utilities/CreateNotification';
import { validateEmail } from 'utils/validateEmail';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import SalutationModal from 'components/Dashboard/Influence/CrmContacts/SalutationModal';
import { useCollapse } from 'react-collapsed';
import { useState } from 'react';
import { SaveButton } from 'components/Dashboard/components/Share';
import useIsMobile from 'components/Dashboard/utilities/useIsMobile';
import StakeholderTagsPopup, { StakeholderTagsAddContactPopup } from './StakeholderTagsPopup';
import useAddTagsToStakeholder from 'components/Dashboard/StakeHolders/StakeholderPage/useAddTagsToStakeholder';
import parliamentarianUsers, { dataHubUsers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import { store } from 'components/Store';
import {
  ContactHolMemberInfoIcon,
  contactHolMemberCondition,
} from 'components/Dashboard/Influence/CrmContacts/CrmContactsTable/CrmContactItem/CrmContactItem';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import ChampionStatus from 'components/Dashboard/Influence/tableComponents/ChampionStatus';
import getAppSite from 'utils/getAppSite';
import { getOS } from 'utils/getOS';
import Notes from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/Notes';
import StakeholderEmailAddresses from 'components/Dashboard/StakeHolders/StakeholderPage/ui/StakeholderEmailAddresses';
import Emails from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/Emails';
import Groups from 'components/Dashboard/StakeHolders/StakeholderPage/ui/BioSections/Groups';
import { Portal } from 'react-portal';
import useLogoUrlReplacement from 'components/Dashboard/UserContent/ui/useLogoUrlReplacement';
import useStakeholderBioTabCounts from 'components/Dashboard/StakeHolders/StakeholderPage/useStakeholderBioTabCounts';
import CustomFieldItem from './CustomFieldItem';

const BioElements = React.forwardRef((props, ref) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team } = state;

  const {
    StakeholderInformation,
    activePlan,
    sendEmailToContact,
    setActiveTab,
    activeTab,
    setShowContactsPopup,
    setShowStakeholderCrmPopup,
    bioElementsScrollBarPosition,
    updateChampionStatus,
    bioSectionContainer,
  } = props;
  const {
    party,
    constituency,
    email,
    website,
    positions,
    selectCommittees,
    lordsSelectCommittees,
    allPartyGroups,
    profile,
    contactInformation,
    categories,
    staff,
  } = StakeholderInformation;

  const [selectedVisibility, setSelectedVisibility] = useState('user');
  const [stakeholderEmails, setStakeholderEmails] = useState([]);
  const { isAuthenticated, user } = useAuth0();
  const isMobile = useIsMobile();
  const { code, enableCrm } = activePlan;

  const basicInfoCondition =
    constituency !== null ||
    party !== null ||
    email !== null ||
    website !== null ||
    (!parliamentarianUsers(code) && !!contactInformation?.[0]);
  const isAddedToMyContacts = contactInformation?.length > 0;

  const { newAgencyPlan } = useTopicOrClientWord();
  const { generalApiCall } = useGeneralApiCall();
  const { setContentResults } = useContentResults();

  const { notesFunctions, emailsFunctions, notesCount, emailsCount, groupsCount, countsReady } =
    useStakeholderBioTabCounts({
      StakeholderInformation,
      enabled: isAuthenticated && enableCrm && !parliamentarianUsers(code),
    });

  const saveCrmContactField = useCallback(
    async (fieldKey, value) => {
      const contact = contactInformation?.[0];
      if (!contact) return;
      if (fieldKey === 'emailAddress' && value && !validateEmail(value)) {
        createNotification('danger', 'Please enter a valid email address');
        throw new Error('Invalid email');
      }
      const result = await generalApiCall({
        pathname: '/api/crm-contact/update',
        method: 'post',
        requestProperties: {
          id: contact.id,
          [fieldKey]: fieldKey === 'emailAddress' && !value ? '' : value,
        },
        needsAuthentication: true,
        returnCompleteRequest: true,
      });
      if (result?.status === 200) {
        const originalKey = fieldKey === 'salutation' ? 'originalSalutation' : 'originalEmailAddress';
        const contactField = fieldKey === 'salutation' ? 'salutation' : 'email';
        const isEmailField = fieldKey !== 'salutation';
        let updatedFields;
        if (!value) {
          updatedFields = {
            [contactField]: contact[originalKey] ?? null,
            ...(isEmailField ? { isCustomEmailAddress: false } : { isCustomSalutation: false }),
          };
        } else if (contact[originalKey] == null) {
          updatedFields = {
            [contactField]: value,
            [originalKey]: contact[contactField] ?? null,
            ...(isEmailField ? { isCustomEmailAddress: true } : { isCustomSalutation: true }),
          };
        } else {
          updatedFields = {
            [contactField]: value,
            ...(isEmailField ? { isCustomEmailAddress: true } : { isCustomSalutation: true }),
          };
        }
        setContentResults((prev) => {
          const Stakeholders = prev?.Stakeholders;
          const newContactInfo = [...(Stakeholders?.contact?.contactInformation ?? [])];
          newContactInfo[0] = { ...newContactInfo[0], ...updatedFields };
          return {
            ...prev,
            Stakeholders: {
              ...Stakeholders,
              contact: { ...Stakeholders?.contact, contactInformation: newContactInfo },
            },
          };
        });
        createNotification('success', `${fieldKey === 'salutation' ? 'Salutation' : 'Email'} updated successfully`);
      } else {
        if (result?.status === 400) {
          const msg = typeof result?.data === 'string' ? result.data : 'Could not save changes';
          createNotification('danger', msg);
        }
        throw new Error('Save failed');
      }
    },
    [contactInformation]
  );

  const dataHubUser = dataHubUsers(activePlan);
  const sectionCommitteesCondition = categories?.includes('MP') || categories?.includes('Lord');
  const BioElementTabsProperties = {
    activeTab,
    setActiveTab,
    setShowContactsPopup,
    setShowStakeholderCrmPopup,
    newAgencyPlan,
    selectedVisibility,
    setSelectedVisibility,
    user,
    stakeholderEmails,
    bioSectionContainer,
    StakeholderInformation,
    notesCount,
    emailsCount,
    groupsCount,
    countsReady,
  };

  return (
    <>
      <div ref={ref}>
        <div className={`box-top-rounded biography-container`}>
          {/* PROFILE IMAGE AND PARTY */}
          {isMobile ? (
            <BioElementsTabs {...BioElementTabsProperties} />
          ) : (
            <div
              className={`sticky-item box-top-rounded border-bottom-none ${
                bioElementsScrollBarPosition > 0 ? 'activity-title-scrolled' : ''
              }`}
            >
              <ProfileImage
                StakeholderInformation={StakeholderInformation}
                setShowContactsPopup={setShowContactsPopup}
                setShowStakeholderCrmPopup={setShowStakeholderCrmPopup}
                activePlan={activePlan}
              />
              <BioElementsTabs {...BioElementTabsProperties} />
            </div>
          )}

          {/* INFORMATION BOX */}
          <div className='biography-secondary-container border-right border-left'>
            {activeTab === 'Profile' && (
              <>
                <div
                  className={`${
                    basicInfoCondition ? 'px-4 border-bottom pb-3' : 'basic-information-empty-adjustment'
                  } ${parliamentarianUsers(code) ? 'pt-4' : ''}`}
                >
                  <BasicInformation
                    StakeholderInformation={StakeholderInformation}
                    crmEmail={!parliamentarianUsers(code) ? contactInformation?.[0] : null}
                    onSaveEmail={(value) => saveCrmContactField('emailAddress', value)}
                  />
                  {isAuthenticated && !parliamentarianUsers(code) && !!contactInformation?.[0] && (
                    <>
                      <CustomFieldItem
                        labelNode={<SalutationHeader />}
                        name='salutation'
                        initialValue={contactInformation[0].salutation}
                        onSave={(value) => saveCrmContactField('salutation', value)}
                      />
                    </>
                  )}
                </div>

                {isAuthenticated && !parliamentarianUsers(code) && !!contactInformation?.[0] && (
                  <div className='px-4 border-bottom py-4'>
                    <h4 className='category-subtitle member-info-subtitle'>
                      Champion status {newAgencyPlan ? `for ${team?.teamName}` : ''}{' '}
                      {dataHubUser && (
                        <span
                          className='icon-padlock ml-2 text-main-blue-40 paragraph-p3 d-inline-block'
                          style={{ transform: 'translateY(1px)' }}
                        />
                      )}
                    </h4>
                    <div className='pt-1 col-lg-8 px-0'>
                      <ChampionStatus
                        updateChampionStatus={updateChampionStatus}
                        scrollBarPosition={bioElementsScrollBarPosition}
                        contact={contactInformation?.[0]}
                        disabled={dataHubUser}
                      />
                    </div>
                  </div>
                )}

                {!parliamentarianUsers(code) && (
                  <TagsSection
                    newAgencyPlan={newAgencyPlan}
                    contactInformation={contactInformation}
                    setShowContactsPopup={setShowContactsPopup}
                    StakeholderInformation={StakeholderInformation}
                    isAuthenticated={isAuthenticated}
                    isAddedToMyContacts={isAddedToMyContacts}
                  />
                )}
                <CreateSectionsCommittees field={positions} title='Positions' className='position-subtitle' />
                {getAppSite() !== 'usa' && !!profile && (
                  <BiographyElement
                    profile={profile}
                    isAuthenticated={isAuthenticated}
                    setShowContactsPopup={setShowContactsPopup}
                  />
                )}
                {staff?.length > 0 && <Staffers staff={staff} />}
                {sectionCommitteesCondition && (
                  <>
                    <CreateSectionsCommittees field={selectCommittees} title='Select Committees' />
                    <CreateSectionsCommittees field={lordsSelectCommittees} title='Lords Select Committees' />
                    <CreateSectionsCommittees
                      field={allPartyGroups?.sort(function (a, b) {
                        return a.toLowerCase().localeCompare(b.toLowerCase());
                      })}
                      title='APPGs'
                    />
                  </>
                )}

                {!isAuthenticated && (
                  <div className={`px-4 pt-3 border-bottom`}>
                    <h4 className={`category-subtitle member-info-subtitle`}>Have your voice heard</h4>
                    <p className={`biography-element mb-2`}>
                      Hundreds of MPs, peers and advisers use PolicyMogul to find lobbying material added by campaigners
                      and organisations
                    </p>
                    <p className='pb-4 mb-0'>
                      <Link
                        to='/register?reg_source=stakeholder_page_add-lobbying-material'
                        className='simple-link-button paragraph-p2'
                      >
                        Add your lobbying material to PolicyMogul
                      </Link>
                    </p>
                  </div>
                )}
              </>
            )}
            {activeTab === 'Notes' && (
              <Notes
                StakeholderInformation={StakeholderInformation}
                activePlan={activePlan}
                selectedVisibility={selectedVisibility}
                setSelectedVisibility={setSelectedVisibility}
                notesFunctions={notesFunctions}
              />
            )}
            {activeTab === 'Emails' && (
              <Emails
                StakeholderInformation={StakeholderInformation}
                activePlan={activePlan}
                sendEmailToContact={sendEmailToContact}
                setStakeholderEmails={setStakeholderEmails}
                crmContact={contactInformation?.[0]}
                emailsFunctions={emailsFunctions}
              />
            )}
            {activeTab === 'Groups' && (
              <Groups StakeholderInformation={StakeholderInformation} activePlan={activePlan} />
            )}
          </div>
        </div>
      </div>
    </>
  );
});

const SalutationHeader = () => {
  const [showModal, setShowModal] = useState(false);
  return (
    <>
      <SalutationModal hideSalutationModal={() => setShowModal(false)} showSalutationModal={showModal} />
      <h4
        className='category-subtitle member-info-subtitle d-flex align-items-center'
        style={{ cursor: 'pointer', width: 'fit-content' }}
        onClick={() => setShowModal(true)}
      >
        <span>Salutation</span>
        <span className='icon-info pointer specific-left-margin-6 light-40-text paragraph-p3 d-inline-block specific-top-padding-2' />
      </h4>
    </>
  );
};

const TagsSection = (props) => {
  const {
    newAgencyPlan,
    contactInformation,
    tags: tagsProp,
    StakeholderInformation,
    isAuthenticated,
    isAddedToMyContacts,
    customContact,
  } = props ?? {};
  const tags = tagsProp ? tagsProp : contactInformation?.[0]?.tags;
  const [showTagsModal, setShowTagsModal] = useState(false);
  const [showAddContactsPopup, setShowAddContactsPopup] = useState(false);

  const globalState = useContext(store);
  const { dispatch, state } = globalState;
  const { team, activePlan } = state;
  const { enableCrm } = activePlan;
  const { addTagsToStakeholder } = useAddTagsToStakeholder({ customContact });
  const dataHubUser = dataHubUsers(activePlan);

  let setShowStakeholderCrmPopup = (val) => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'showStakeholderCrmPopup',
      value: val,
    });
  };
  let setShowContactsPopup = (val) => {
    dispatch({
      type: 'MODIFY_SECTION',
      parameter: 'showContactsPopup',
      value: { show: val },
    });
  };

  return (
    <>
      <div className='px-4 py-3 border-bottom'>
        <h4 className='category-subtitle member-info-subtitle'>
          Tags {newAgencyPlan ? `for ${team?.teamName}` : ''}
          {dataHubUser && (
            <span
              className='icon-padlock ml-2 text-main-blue-40 paragraph-p3 d-inline-block'
              style={{ transform: 'translateY(1px)' }}
            />
          )}
        </h4>
        <p className='mb-0 member-data mt-2 pt-1'>
          {tags?.map((item, index) => {
            return (
              <span
                className='d-inline-block mr-2 border rounded pl-2 pr-4 mb-2 py-1 position-relative'
                key={`item-tag-${index}`}
              >
                {item}
                <button
                  className='general-button react-tagsinput-remove'
                  onClick={async (e) => {
                    if (enableCrm) {
                      let el = e.target;
                      el.parentElement.classList.add('tag-button-disabled');
                      el.disabled = true;
                      let newTags = [...tags];
                      newTags.splice(index, 1);
                      await addTagsToStakeholder(null, newTags);
                      el.disabled = false;
                      el.parentElement.classList.remove('tag-button-disabled');
                    } else {
                      setShowStakeholderCrmPopup(true);
                    }
                  }}
                />
              </span>
            );
          })}
          <button
            className={`general-button simple-link-button`}
            onClick={() => {
              if (isAuthenticated) {
                if (enableCrm) {
                  if (isAddedToMyContacts) {
                    setShowTagsModal(true);
                  } else {
                    setShowAddContactsPopup(true);
                  }
                } else {
                  setShowStakeholderCrmPopup(true);
                }
              } else {
                setShowContactsPopup(true);
              }
            }}
            disabled={dataHubUser}
          >
            + Add new tag
          </button>
        </p>
      </div>
      <StakeholderTagsPopup
        setShowPopup={setShowTagsModal}
        showPopUp={showTagsModal}
        StakeholderInformation={StakeholderInformation ?? contactInformation}
        customContact={customContact}
      />
      <StakeholderTagsAddContactPopup
        setShowPopup={setShowAddContactsPopup}
        showPopUp={showAddContactsPopup}
        StakeholderInformation={StakeholderInformation}
      />
    </>
  );
};

const BioElementsTabs = (props) => {
  const {
    setActiveTab,
    activeTab,
    setShowContactsPopup,
    setShowStakeholderCrmPopup,
    newAgencyPlan,
    selectedVisibility,
    setSelectedVisibility,
    user,
    stakeholderEmails,
    bioSectionContainer,
    StakeholderInformation,
    notesCount,
    emailsCount,
    groupsCount,
    countsReady,
  } = props;

  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { activePlan, dataHubNavigationPopup } = state;
  const { code, enableCrm } = activePlan;
  const { isStaff } = StakeholderInformation ?? {};
  const dataHubUser = dataHubUsers(activePlan);
  const { isAuthenticated } = useAuth0();
  const crmTabs = [
    {
      name: isAuthenticated ? 'Notes' : 'Write a note',
      tab: 'Notes',
      count: notesCount,
      icon: () => {
        return (
          <span
            className='icon-briefing-material d-none d-xl-inline-block paragraph-p2'
            style={{ marginRight: '0.65em', verticalAlign: 'middle' }}
          />
        );
      },
    },
    {
      name: 'Groups',
      tab: 'Groups',
      count: groupsCount,
      icon: () => {
        return (
          <span
            className='icon-team d-none d-xl-inline-block paragraph-p2'
            style={{ marginRight: '0.75em', verticalAlign: 'middle', marginTop: '1px' }}
          />
        );
      },
      notShow: !isAuthenticated,
    },
    {
      name: isAuthenticated ? 'Emails' : 'Send an email',
      tab: 'Emails',
      count: emailsCount,
      icon: () => {
        return (
          <span
            className='icon-email d-none d-xl-inline-block paragraph-p4'
            style={{ marginRight: '0.75em', verticalAlign: 'middle', marginTop: '1px' }}
          />
        );
      },
    },
  ];
  return (
    <>
      {!parliamentarianUsers(code) && (
        <div className={`flex-centered justify-content-between p-4`}>
          <div
            className={`flex-centered stakeholder-tabs-container flex-grow-1 ${
              isAuthenticated && !isStaff ? 'justify-content-lg-between' : ''
            }`}
          >
            <button
              className={`general-button biography-controllers-button border rounded-pill  ${
                activeTab === 'Profile' ? 'third-button text-white' : 'fourth-button'
              }`}
              onClick={() => {
                setActiveTab('Profile');
                if (bioSectionContainer) {
                  bioSectionContainer.current.scrollToTop();
                }
              }}
            >
              <span className='icon-profile d-none d-xl-inline-block paragraph-p3' style={{ marginRight: '0.75em' }} />{' '}
              About
            </button>
            {crmTabs
              .filter((item) => !item.notShow)
              ?.map((item) => {
                const { icon, tab, name, count } = item;
                return (
                  <button
                    key={`stakeholder-tab-${tab}`}
                    className={`general-button biography-controllers-button biography-tab-button border rounded-pill specific-left-margin-16 specific-left-margin-xl-10 specific-left-margin-xxl-16  ${
                      activeTab === tab ? 'third-button text-white' : 'fourth-button'
                    }`}
                    onClick={() => {
                      if (isAuthenticated) {
                        if (dataHubUser && (tab === 'Groups' || tab === 'Emails')) {
                          dispatch({
                            type: 'MODIFY_SECTION',
                            parameter: 'dataHubNavigationPopup',
                            value: { ...dataHubNavigationPopup, isOpen: true, allowClose: true },
                          });
                        } else {
                          if (bioSectionContainer) {
                            bioSectionContainer.current.scrollToTop();
                          }
                          if (enableCrm) {
                            setActiveTab(tab);
                          } else {
                            setShowStakeholderCrmPopup(true);
                          }
                        }
                      } else {
                        setShowContactsPopup(true);
                      }
                    }}
                  >
                    <BioTabCountBadge count={count} countsReady={countsReady} icon={icon} />
                    {name}
                  </button>
                );
              })}
          </div>
          {activeTab === 'Emails' && newAgencyPlan && (
            <AgencyLogoAndExplanation
              activeTab={activeTab}
              selectedVisibility={selectedVisibility}
              setSelectedVisibility={setSelectedVisibility}
              user={user}
              stakeholderEmails={stakeholderEmails}
            />
          )}
        </div>
      )}
    </>
  );
};

const formatBioTabCount = (value) => (value > 9 ? '9+' : value);
const bioTabCountDigitClass = (formatted) => {
  if (formatted === '9+') return 'digit-value-plus';
  if (formatted === 1 || formatted === 4) return 'digit-value-left';
  return '';
};
const BioTabCountBadge = ({ count, countsReady, icon }) => {
  const prevCountRef = useRef(count);
  const [digitRoll, setDigitRoll] = useState({ from: count, key: 0 });

  useEffect(() => {
    if (countsReady && count > 0 && count > prevCountRef.current) {
      const from = prevCountRef.current;
      setDigitRoll((roll) => ({ from, key: roll.key + 1 }));
    }
    prevCountRef.current = count;
  }, [count, countsReady]);

  const fromFormatted = formatBioTabCount(digitRoll.from);
  const countFormatted = formatBioTabCount(count);
  const isMac = getOS() === 'MacOS' ? ' is-mac' : '';

  return (
    <span className={`bio-tab-count-roll d-none d-xl-inline-block${countsReady && count > 0 ? ' show-count' : ''}`}>
      <span className='bio-tab-count-roll-track'>
        <span className='bio-tab-count-roll-slot'>
          <span className='flex-centered justify-content-center overflow-hidden bio-tab-count-roll-badge rounded-circle bg-main-blue text-white font-weight-bold'>
            <span className='bio-tab-count-digit-track' key={digitRoll.key}>
              <span className={`flex-centered justify-content-center bio-tab-count-digit ${isMac}`}>
                <span className={`bio-tab-count-digit-value ${bioTabCountDigitClass(countFormatted)}${isMac}`}>
                  {countFormatted}
                </span>
              </span>
              <span className={`flex-centered justify-content-center bio-tab-count-digit ${isMac}`}>
                <span className={`bio-tab-count-digit-value ${bioTabCountDigitClass(fromFormatted)}${isMac}`}>
                  {fromFormatted}
                </span>
              </span>
            </span>
          </span>
        </span>
        <span className='bio-tab-count-roll-slot'>{icon()}</span>
      </span>
    </span>
  );
};

const ProfileImage = (props) => {
  const globalState = useContext(store);
  const { dispatch } = globalState;

  const { StakeholderInformation } = props;
  const { categories, imageId, imageUrl, party, officalName, summary, isStaff } = StakeholderInformation;
  const summaryRef = useRef(null);
  const [isSummaryTruncated, setIsSummaryTruncated] = useState(false);

  useEffect(() => {
    const checkTruncation = () => {
      if (summaryRef.current) {
        const isTruncated = summaryRef.current.scrollHeight > summaryRef.current.clientHeight;
        setIsSummaryTruncated(isTruncated);
      }
    };

    checkTruncation();
    // Recheck on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [summary]);

  const createMainBarHouse = () => {
    if (categories?.includes('MP')) {
      return 'bar-commons';
    } else if (categories?.includes('Lord')) {
      return 'bar-lords';
    } else if (categories?.includes('Special Adviser') || categories?.includes('Special Advisor')) {
      return 'bar-special-adviser';
    } else {
      return 'bar-special-adviser';
    }
  };

  const partyClass = () => `member-${party !== null ? normalize(party?.toLowerCase()) : ''}-picture`;
  const isMobile = useIsMobile();
  return (
    <div
      className={`d-flex align-items-center profile-image-content py-3 px-4 profile-image-bar ${createMainBarHouse()} profile-image-${partyClass()} ${
        isStaff ? 'bg-main-blue text-white' : ''
      }`}
    >
      <img
        className={`${partyClass()} profile-main-image`}
        alt='stakeholder profile'
        src={
          imageUrl ??
          (imageId !== 0 && imageId !== null && !idsWithoutImages.includes(imageId)
            ? `https://www.parliamenttoday.com/image.php?imageid=${imageId}&ml=1`
            : `${import.meta.env.VITE_APP_CDNURL}/images/20210722-stakeholders-placeholder.png`)
        }
      />
      <div className='position-relative pt-2 pl-3 flex-grow-1' style={{ zIndex: 100 }}>
        <div className='profile-main-subtext'>
          <div className='d-flex align-items-center align-items-lg-start justify-content-between'>
            <div className='flex-grow-1 '>
              <h3 className='main-title mb-1'>{officalName}</h3>
              {!isMobile && (
                <p
                  ref={summaryRef}
                  className='mb-0 hidden-lines hidden-two-lines'
                  {...(isSummaryTruncated && {
                    'data-tooltip-content': summary,
                    'data-tooltip-id': 'general-tooltip',
                    'data-tooltip-class-name': 'general-tooltip pricing-feature-tooltip tooltip-width-300',
                  })}
                >
                  {summary}
                </p>
              )}
            </div>
            <div className='profile-share-buttons pl-2 flex-centered'>
              <SaveButton
                item={{
                  id: StakeholderInformation.id,
                  objectID: StakeholderInformation.objectId,
                  type: StakeholderInformation.objectType,
                  party,
                  title: officalName,
                  imageId,
                }}
              />
              <button
                className='general-button circle-btn-with-hover bg-white ml-2'
                data-tooltip-content={'Share profile'}
                data-tooltip-id={'general-tooltip'}
                onClick={(e) => {
                  e.target.blur();
                  dispatch({
                    type: 'MODIFY_SECTION',
                    parameter: 'shareStakeholderPopupProps',
                    value: {
                      title: StakeholderInformation.officalName,
                      header: 'Share this profile',
                      urlToShare: `${window.location.origin}${StakeholderInformation.canonicalUrl}`,
                      show: true,
                    },
                  });
                }}
              >
                <span
                  className='icon-share d-block paragraph-p3'
                  style={{
                    transform: 'rotate(350deg) scale(1.05)',
                  }}
                ></span>
              </button>
            </div>
          </div>
        </div>
        {isMobile && <p className='mb-0 mt-2 hidden-lines hidden-two-lines'>{summary}</p>}
      </div>
    </div>
  );
};

const BasicInformation = (props) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { activePlan } = state;
  const { enableStakeholders, code } = activePlan;
  const { StakeholderInformation, crmEmail, onSaveEmail } = props;
  const { party, constituency, email, website, ministerialEmailAddress, positionCategories } = StakeholderInformation;

  const location = useLocation();
  const { isAuthenticated } = useAuth0();

  return (
    <>
      {constituency !== null && (
        <>
          <h4 className='category-subtitle member-info-subtitle'>
            {getAppSite() === 'usa' ? 'District' : 'Constituency'}
          </h4>
          <p className='mb-0 member-data'>{constituency}</p>
        </>
      )}
      {party !== null && (
        <>
          <h4 className='category-subtitle member-info-subtitle'>Political party</h4>
          <p className='mb-0 member-data'>{party}</p>
        </>
      )}
      {website !== null && (
        <>
          <h4 className='category-subtitle member-info-subtitle'>Personal website</h4>
          <p className='mb-0 member-data'>
            <a className='external-url' href={website} rel='noopener noreferrer' target='_blank'>
              {website}
              <button
                className='general-button member-email-button external-url-button'
                data-tooltip-content='Open website'
                data-tooltip-id='general-tooltip'
              >
                <span className='icon-external-link paragraph-p5 light-60-text font-weight-normal ' />
              </button>
            </a>
          </p>
        </>
      )}
      {(email !== null || crmEmail) && (
        <>
          {crmEmail ? (
            <StakeholderEmailAddresses
              crmEmail={crmEmail}
              ministerialEmailAddress={ministerialEmailAddress}
              positionCategories={positionCategories}
              onSaveEmail={onSaveEmail}
            />
          ) : (
            <>
              <h4 className='category-subtitle member-info-subtitle'>Email</h4>
              {isAuthenticated && (
                <>
                  {(parliamentarianUsers(code) || enableStakeholders) && (
                    <>
                      <p className='mb-0 member-data'>
                        <a
                          className='external-url mail-url'
                          href={`mailto:${email}`}
                          id={'stakeholder-email-external-link'}
                        >
                          {email}
                        </a>
                        {contactHolMemberCondition(email) && (
                          <ContactHolMemberInfoIcon
                            className='paragraph-p3 ml-2 d-inline-block'
                            style={{ color: 'rgba(0, 18, 43, 0.55)', transform: 'translateY(1px)' }}
                          />
                        )}
                        <button
                          className='general-button member-email-button'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            let tempInput = document.createElement('input');
                            tempInput.style = 'position: absolute; left: -1000px; top: -1000px;';
                            tempInput.value = email;
                            document.body.appendChild(tempInput);
                            tempInput.select();
                            document.execCommand('copy');
                            document.body.removeChild(tempInput);
                            createNotification('success', `Email address has been copied`);
                          }}
                          data-tip='Copy email address'
                          data-for='general-tooltip'
                        >
                          <span className='icon-copy-thin paragraph-p4 light-60-text font-weight-normal d-inline-block' />
                        </button>
                      </p>
                    </>
                  )}
                  {!enableStakeholders && (
                    <Link className='simple-link-button' to={`/pricing?returnUrl=${location.pathname}`}>
                      Upgrade to Mogul to reveal contact info
                    </Link>
                  )}
                </>
              )}
              {!isAuthenticated && (
                <button
                  className='general-button simple-link-button'
                  onClick={() => {
                    window.location.assign(`/register?reg_source=stakeholder_page_see_email`);
                  }}
                >
                  Sign up to reveal contact info
                </button>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

const CreateSectionsCommittees = (props) => {
  const { field, title, className } = props;
  const [isExpanded, setExpanded] = useState(true);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  return (
    <>
      {field !== null && field !== undefined && field && field.length > 0 && (
        <div className={`border-bottom ${isExpanded ? 'pb-3' : ''}`}>
          <div
            className={`${isExpanded ? 'pt-3' : 'py-3'} pointer px-4 toggle-no-button`}
            {...getToggleProps({
              onClick: () => setExpanded((prevExpanded) => !prevExpanded),
            })}
          >
            <h4 className={`feature-subtitle category-subtitle dropdown-item-element ${isExpanded ? 'open' : ''}`}>
              {title}
            </h4>
          </div>
          <div {...getCollapseProps()} className='px-4'>
            <div className='pt-1'>
              {field.map((item, index) => (
                <div className='my-2 member-data' key={`comittee-${index}`}>
                  <p className={`mb-0 committe-subtitle ${className ?? ''}`}>
                    <span
                      className={`icon-${className === 'position-subtitle' ? 'star light-75-text' : 'group light-60-text'}  d-inline-block-centered centered-5 specific-right-margin-10`}
                    />
                    {item}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const BiographyElement = (props) => {
  const [isExpanded, setExpanded] = useState(true);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  return (
    <div className={`border-bottom ${isExpanded ? 'pb-3' : ''}`}>
      <div
        className={`${isExpanded ? 'pt-3' : 'py-3'} pointer px-4 toggle-no-button`}
        {...getToggleProps({
          onClick: () => setExpanded((prevExpanded) => !prevExpanded),
        })}
      >
        <h4 className={`category-subtitle member-info-subtitle dropdown-item-element ${isExpanded ? 'open' : ''}`}>
          Biography
        </h4>
      </div>
      <div {...getCollapseProps()} className='px-4'>
        <BiographyContent {...props} />
      </div>
    </div>
  );
};

const Staffers = (props) => {
  const { staff } = props;
  const [isExpanded, setExpanded] = useState(true);
  const [isExpandedShowMore, setExpandedShowMore] = useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });
  const { getCollapseProps: collapsePropsShowMore, getToggleProps: togglePropsShowMore } = useCollapse({
    isExpandedShowMore,
  });

  const firstStaffers = staff.slice(0, 10);
  const remainingStaffers = staff.slice(10);

  return (
    <div className={`border-bottom ${isExpanded ? 'pb-3' : ''}`}>
      <div
        className={`${isExpanded ? 'pt-3' : 'py-3'} pointer px-4 toggle-no-button`}
        {...getToggleProps({
          onClick: () => setExpanded((prevExpanded) => !prevExpanded),
        })}
      >
        <h4 className={`category-subtitle member-info-subtitle dropdown-item-element ${isExpanded ? 'open' : ''}`}>
          Staff
        </h4>
      </div>
      <div {...getCollapseProps()} className='px-4'>
        {firstStaffers && (
          <div className='pt-1'>
            {firstStaffers.map((staffer, index) => {
              return <StaffersElement staffer={staffer} key={`first-staffer-${index}`} />;
            })}
          </div>
        )}
        {remainingStaffers?.length > 0 && (
          <>
            <div {...collapsePropsShowMore()}>
              {remainingStaffers.map((staffer, index) => {
                return <StaffersElement staffer={staffer} key={`remaining-staffer-${index}`} />;
              })}
            </div>
            <div
              {...togglePropsShowMore({
                onClick: () => setExpandedShowMore((prevExpanded) => !prevExpanded),
              })}
              className='mt-2'
            >
              <span
                className={`light-60-text dropdown-item-element paragraph-p2 dropdown-item-element-9 d-inline-block ${
                  isExpandedShowMore ? 'open' : ''
                }`}
              >
                {isExpandedShowMore ? 'Show less' : 'Show more'}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const StaffersElement = (props) => {
  const { staffer } = props;
  const { officalName, id } = staffer ?? {};
  const isDeleted = officalName?.endsWith('(Deleted)');

  const content = (
    <p className={`mb-0`}>
      <span className='icon-profile mr-2 d-inline-block-centered' />
      {officalName}
    </p>
  );

  return (
    <div className='my-2 member-data flex-centered'>
      {isDeleted ? (
        content
      ) : (
        <Link className='reset-link light-70-text general-link ' to={`/stakeholders/${id}/${normalize(officalName)}`}>
          {content}
        </Link>
      )}
    </div>
  );
};

const BiographyContent = (props) => {
  const { profile, isAuthenticated, setShowContactsPopup } = props;
  const [activeEllipsis, setActiveEllipsis] = useState(false);
  const location = useLocation();
  const createMarkup = (body) => {
    return { __html: body };
  };
  const [isExpanded, setExpanded] = useState(false);
  const { getCollapseProps, getToggleProps } = useCollapse({ isExpanded });

  const isEllipsisActive = (e) => {
    if (e) {
      return e.offsetHeight < e.scrollHeight;
    }
  };

  const bioElement = useCallback((node) => {
    if (node !== null) {
      setActiveEllipsis(isEllipsisActive(node));
    }
  }, []);

  return (
    <>
      <div className='pt-1'>
        <div
          dangerouslySetInnerHTML={createMarkup(profile)}
          className={`biography-element hidden-lines biography-element-hidden ${isExpanded ? 'd-none' : ''}`}
          ref={bioElement}
        />
      </div>

      <div {...getCollapseProps()}>
        <div
          className={`biography-element ${isExpanded ? 'd-block' : 'd-none'}`}
          dangerouslySetInnerHTML={createMarkup(profile)}
        ></div>
      </div>
      <p
        className='mb-2 text-light-blue paragraph-p2 pointer toggle-no-button'
        style={{ visibility: activeEllipsis || isExpanded ? 'visible' : 'hidden' }}
        {...getToggleProps({
          onClick: () => {
            if (isAuthenticated) {
              setExpanded((prevExpanded) => !prevExpanded);
            } else {
              setShowContactsPopup({
                show: true,
                url: `/register?reg_source=stakeholder-see-more&returnUrl=${location.pathname}`,
              });
            }
          },
        })}
      >
        See {isExpanded ? 'less' : 'more'}
      </p>
    </>
  );
};

const AgencyLogoAndExplanation = ({
  activeTab,
  selectedVisibility,
  setSelectedVisibility,
  user,
  stakeholderEmails,
  positionX,
  positionY,
  scrollbarRef,
  buttonClass,
  contentType,
}) => {
  const globalState = useContext(store);
  const { state } = globalState;
  const { team, activePlan } = state;
  const { replacementOfUrl } = useLogoUrlReplacement();
  return activeTab === 'Notes' ? (
    <NoteVisibilitySelect
      user={user}
      team={team}
      selectedVisibility={selectedVisibility}
      setSelectedVisibility={setSelectedVisibility}
      activePlan={activePlan}
      positionX={positionX}
      positionY={positionY}
      scrollbarRef={scrollbarRef}
      buttonClass={buttonClass}
      contentType={contentType}
    />
  ) : (
    <>
      {stakeholderEmails?.length > 0 && (
        <img
          style={{ width: '30px', height: '30px' }}
          alt='avatar'
          data-tooltip-place={'top'}
          data-tooltip-content={
            'Emails logged for this stakeholder are specific to this client. To send emails for a different client, switch using the top navigation'
          }
          data-tooltip-id='general-tooltip'
          data-tooltip-class-name={'general-tooltip pricing-feature-tooltip report-popup-tooltip'}
          className='rounded-circle img-fluid'
          src={
            team?.organisationLogo
              ? replacementOfUrl({ url: team?.organisationLogo })
              : `${import.meta.env.VITE_APP_CDNURL}/images/20230909-company-placeholder.png`
          }
        />
      )}
    </>
  );
};

const NoteVisibilitySelect = (props) => {
  const {
    user,
    team,
    selectedVisibility,
    setSelectedVisibility,
    disabled,
    activePlan,
    positionY = 'bottom',
    positionX = 'right',
    scrollbarRef,
    buttonClass = '',
    contentType,
  } = props;
  const isMobile = useIsMobile();
  const [showActions, setShowActions] = useState(false);

  const actionsElement = useRef(null);
  const getDropdownPosition = () => {
    const { width, top, left, height, right } = actionsElement?.current?.getBoundingClientRect() ?? {};
    return {
      top: positionY === 'bottom' ? top + height + 5 : top - 264,
      left: positionX === 'right' ? left + width - (isMobile ? 320 : 117) : right - 320,
    };
  };
  const [dropdownPosition, setDropdownPosition] = useState(getDropdownPosition());
  const actionsDropdownElement = useRef(null);
  const hideActions = () => {
    setShowActions(false);
  };

  const updateDropdownPosition = () => {
    setDropdownPosition(getDropdownPosition());
  };

  const handleScroll = () => {
    if (showActions) {
      updateDropdownPosition();
    }
  };

  useEffect(() => {
    const scrollElement = scrollbarRef?.current?.view;
    if (scrollElement && showActions) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollbarRef, showActions]);

  useEffect(() => {
    if (showActions) {
      updateDropdownPosition();
    }
  }, [showActions]);

  return (
    <div className='position-relative note-visibility-select' ref={actionsElement}>
      <button
        className={`general-button rounded-button ${disabled ? 'default-cursor' : 'd-flex'} ${buttonClass}`}
        onClick={() => {
          if (!disabled) {
            setShowActions(!showActions);
          }
        }}
      >
        {selectedVisibility === 'user' ? (
          <AgencyImage team={team} />
        ) : (
          <OrganizationImage activePlan={activePlan} user={user} />
        )}
        {!disabled && (
          <span className={`dropdown-item-element dropdown-item-element-9 pr-3 ${showActions ? 'open' : ''}`}></span>
        )}
      </button>
      {showActions && (
        <Portal node={document && document.querySelector('body')}>
          <div className='general-close-background z-extra-max' onClick={() => hideActions()}></div>
          <div
            className='position-fixed rounded border bg-main-white box-outstanding adjustment-of-position py-2'
            style={{
              width: '320px',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
            }}
            ref={actionsDropdownElement}
          >
            <div
              className='d-flex px-4 py-3 pointer action-state-hover'
              onClick={(e) => {
                setSelectedVisibility('user');
                hideActions();
              }}
            >
              <div className='pt-1 analytics-share-icon-container' style={{ minWidth: '30px', marginTop: '-4px' }}>
                <AgencyImage team={team} style={{ width: '30px', height: '30px' }} />
              </div>
              <div className='pl-3'>
                <p className='font-weight-bold mb-1'>{team?.expires ? 'Prospect' : 'Client'}-specific note</p>
                <p className='main-light-text mb-0'>This note is specific to your work with {team?.teamName}</p>
              </div>
            </div>
            {contentType !== 'CrmContact' && (
              <div
                className='d-flex px-4 py-3 pointer action-state-hover'
                onClick={(e) => {
                  setSelectedVisibility('agency');
                  hideActions();
                }}
              >
                <div className='pt-1 analytics-share-icon-container' style={{ minWidth: '30px', marginTop: '-4px' }}>
                  <OrganizationImage activePlan={activePlan} user={user} style={{ width: '30px', height: '30px' }} />
                </div>
                <div className='pl-3'>
                  <p className='font-weight-bold mb-1'>General note</p>
                  <p className='main-light-text mb-0'>This note is not specific to any particular client or prospect</p>
                </div>
              </div>
            )}
          </div>
        </Portal>
      )}
    </div>
  );
};

const OrganizationImage = ({ style = {}, activePlan, user }) => {
  const { replacementOfUrl } = useLogoUrlReplacement();
  return (
    <img
      style={{ width: '20px', height: '20px', ...style }}
      alt='avatar'
      className='rounded-circle img-fluid'
      src={activePlan?.organisationLogo ? replacementOfUrl({ url: activePlan?.organisationLogo }) : user.picture}
    />
  );
};
const AgencyImage = ({ style = {}, team }) => {
  const { replacementOfUrl } = useLogoUrlReplacement();
  return (
    <img
      style={{ width: '20px', height: '20px', ...style }}
      alt='avatar'
      className='rounded-circle img-fluid'
      src={
        team?.organisationLogo
          ? replacementOfUrl({ url: team?.organisationLogo })
          : `${import.meta.env.VITE_APP_CDNURL}/images/20230909-company-placeholder.png`
      }
    />
  );
};

export {
  ProfileImage,
  BioElementsTabs,
  TagsSection,
  OrganizationImage,
  AgencyImage,
  AgencyLogoAndExplanation,
  BasicInformation,
  CustomFieldItem,
};
export default BioElements;
